import {
  MED_SKU,
  getEligibleMedProducts,
  getBMI,
  isFulldoseGlp1Eligible,
} from "src/utils/medEligibility";
import { MED_BMI_SPECIAL_LOW_MIN } from "src/components/refactored-survey/question-sets/insurance-survey-questions/utils/insuranceConstants";
import {
  isQualifiedForPersonalizedDosing,
  hasWeightLossGoal,
} from "src/utils/userSegment/features";

export enum NON_MED_PLAN {
  NOOM_WEIGHT = "NOOM_WEIGHT",
  FREEMIUM = "FREEMIUM",
}

export type HealthPlan = MED_SKU | NON_MED_PLAN;

const HEALTH_GLP1_PLANS: readonly MED_SKU[] = [
  MED_SKU.COMPOUNDED,
  MED_SKU.GENERIC_LIRAGLUTIDE,
  MED_SKU.COMPOUNDED_MICRODOSE,
  MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER,
] as const;

/**
 * Checks if user is eligible for GLP-1 program according to health route recommendation logic.
 * GLP-1 eligible includes: Compounded, Liraglutide, Microdose, and Proactive Health plans.
 *
 * NOTE: This function is specific to health route recommendation logic.
 */
function isGlp1Eligible(eligibleProducts: MED_SKU[]): boolean {
  return eligibleProducts.some((sku) => HEALTH_GLP1_PLANS.includes(sku));
}

export function getRecommendedHealthPlan(): HealthPlan | undefined {
  const eligibleProducts = getEligibleMedProducts();
  const isWeightLossGoal = hasWeightLossGoal();
  const bmi = getBMI();

  if (bmi < MED_BMI_SPECIAL_LOW_MIN) {
    return isWeightLossGoal ? NON_MED_PLAN.NOOM_WEIGHT : NON_MED_PLAN.FREEMIUM;
  }

  const glp1Eligible = isGlp1Eligible(eligibleProducts);
  const proactiveHealthEligible = eligibleProducts.includes(
    MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER
  );
  const oralsEligible = eligibleProducts.includes(MED_SKU.ORALS_METFORMIN);
  const brandedEligible = eligibleProducts.includes(MED_SKU.BRANDED);

  const fullDoseGlp1Eligible = isFulldoseGlp1Eligible(eligibleProducts);

  if (glp1Eligible) {
    // No weight loss goal: Proactive Health or Freemium (applies to both full-dose and non-full-dose cases)
    if (!isWeightLossGoal) {
      if (proactiveHealthEligible) {
        return MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER;
      }
      return NON_MED_PLAN.FREEMIUM;
    }

    // Full-dose GLP-1 (Compounded/Liraglutide/Microdose) if weight loss goal
    if (fullDoseGlp1Eligible && isWeightLossGoal) {
      if (
        isQualifiedForPersonalizedDosing() &&
        eligibleProducts.includes(MED_SKU.COMPOUNDED)
      ) {
        return MED_SKU.COMPOUNDED;
      }
      if (eligibleProducts.includes(MED_SKU.GENERIC_LIRAGLUTIDE)) {
        return MED_SKU.GENERIC_LIRAGLUTIDE;
      }
      if (eligibleProducts.includes(MED_SKU.COMPOUNDED_MICRODOSE)) {
        return MED_SKU.COMPOUNDED_MICRODOSE;
      }
    }

    // Without full-dose: Proactive Health (priority) or Orals fallback
    if (!fullDoseGlp1Eligible) {
      if (proactiveHealthEligible) {
        return MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER;
      }
      // Proactive not eligible, but Orals eligible -> recommend Orals
      if (oralsEligible) {
        return MED_SKU.ORALS_METFORMIN;
      }
      // If GLP-1 eligible but no Proactive/Orals -> Noom Weight fallback
      return NON_MED_PLAN.NOOM_WEIGHT;
    }
  }

  if (isWeightLossGoal) {
    if (brandedEligible) {
      return MED_SKU.BRANDED;
    }
    if (oralsEligible) {
      return MED_SKU.ORALS_METFORMIN;
    }
    return NON_MED_PLAN.NOOM_WEIGHT;
  }

  // No weight loss goal: Freemium (no medication plans recommended)
  return NON_MED_PLAN.FREEMIUM;
}
